import React, { useEffect, useState } from "react";
import { CalendarClock, MapPin, Clock, Tag } from "lucide-react";
import api from "@/lib/api";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const pad2 = (n) => String(n).padStart(2, "0");

// Format a training date range as "DD-DD MMM YYYY" (or "DD MMM YYYY" for a single day).
export function formatTrainingRange(startIso, endIso) {
  if (!startIso) return "";
  const s = new Date(startIso);
  const sd = s.getUTCDate(), sm = s.getUTCMonth(), sy = s.getUTCFullYear();
  if (endIso) {
    const e = new Date(endIso);
    const ed = e.getUTCDate(), em = e.getUTCMonth(), ey = e.getUTCFullYear();
    const sameDay = ed === sd && em === sm && ey === sy;
    if (!sameDay) {
      if (sm === em && sy === ey) return `${pad2(sd)}-${pad2(ed)} ${MONTHS[sm]} ${sy}`;
      if (sy === ey) return `${pad2(sd)} ${MONTHS[sm]} - ${pad2(ed)} ${MONTHS[em]} ${sy}`;
      return `${pad2(sd)} ${MONTHS[sm]} ${sy} - ${pad2(ed)} ${MONTHS[em]} ${ey}`;
    }
  }
  return `${pad2(sd)} ${MONTHS[sm]} ${sy}`;
}

export default function Training() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/training").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="training-page">
      <div className="eyebrow mb-1" style={{ letterSpacing: "0.16em" }}>Battle Rhythm</div>
      <h1 className="heading text-4xl mb-6">Training</h1>
      <div className="space-y-3">
        {events.map((e) => {
          const range = formatTrainingRange(e.date, e.end_date);
          return (
            <div key={e.id} className="rsp-card p-4" data-testid={`training-${e.id}`}>
              {range && (
                <div className="flex items-center gap-1.5 mb-1.5" data-testid={`training-date-${e.id}`}>
                  <CalendarClock size={14} className="gold shrink-0" />
                  <span className="font-mono-rsp text-[var(--rsp-gold)] text-sm tracking-wide">{range}</span>
                </div>
              )}
              <div className="min-w-0">
                <h3 className="font-head text-xl uppercase tracking-wide break-words">{e.title}</h3>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[var(--rsp-gray)] text-sm mt-1">
                  {e.time && <span className="flex items-center gap-1 break-words min-w-0"><Clock size={13} className="shrink-0" /> {e.time}</span>}
                  {e.location && <span className="flex items-center gap-1 break-words min-w-0"><MapPin size={13} className="shrink-0" /> {e.location}</span>}
                  {e.category && <span className="flex items-center gap-1 gold break-words min-w-0"><Tag size={13} className="shrink-0" /> {e.category}</span>}
                </div>
                {e.notes && <p className="text-[var(--rsp-gray)] text-sm mt-2 leading-relaxed break-words">{e.notes}</p>}
              </div>
            </div>
          );
        })}
        {events.length === 0 && (
          <div className="rsp-card p-8 text-center text-[var(--rsp-muted)]">
            <CalendarClock className="mx-auto mb-2 opacity-50" /> No training events scheduled.
          </div>
        )}
      </div>
    </div>
  );
}
